import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  Calculator,
  Check,
  FileText,
  Loader2,
  Search,
  ShieldCheck,
  User,
  X
} from 'lucide-react';
import { Button } from './ui/button';
import { api } from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import { Toast } from './ui/Toast';
import { LongToast, shouldUseLongToast } from './ui/LongToast';
import ToastPortal from './ToastPortal';

const formatMontant = (montant) => (
  `${new Intl.NumberFormat('fr-FR').format(Math.round(Number(montant || 0)))} FCFA`
);

/**
 * L'option « Valeur famille » n'existe que si la famille en porte une.
 *
 * Beaucoup de familles n'en ont pas : la valeur est facultative, et le nettoyage
 * a remis à zéro celles que plus personne ne recevait. Proposer de reprendre une
 * valeur nulle reviendrait à proposer de ne rien appliquer sous un autre nom — et
 * la décrire sans jamais l'afficher laissait deviner un montant qui n'existait pas.
 */
const getReductionModes = (valeurFamilleMensuelle) => [
  ...(valeurFamilleMensuelle > 0 ? [{
    value: 'famille',
    title: 'Valeur famille',
    description: `L'élève reprendra la valeur de cette famille : ${formatMontant(valeurFamilleMensuelle)} de réduction par mois.`
  }] : []),
  {
    value: 'conserver',
    title: 'Conserver',
    description: "Garde la situation actuelle de l'élève avant son rattachement."
  },
  {
    value: 'personnalisee',
    title: 'Nouvelle réduction',
    description: 'Un montant décidé pour ce seul élève.'
  },
  {
    value: 'aucune',
    title: 'Aucune',
    description: "L'élève paie le tarif officiel de sa classe."
  }
];

// Taille commune aux deux boutons du pied : un bouton d'annulation plus petit que
// le bouton d'envoi se lit comme une hiérarchie qui n'existe pas ici.
const BOUTON_PIED_CLASSES = 'h-9 min-w-[168px] justify-center gap-2';

// « Octobre, Novembre et Décembre » plutôt qu'une énumération à la virgule : la
// phrase est lue par une gestionnaire, pas parcourue comme une liste.
const listerMois = (noms = []) => {
  if (noms.length <= 1) return noms.join('');
  return `${noms.slice(0, -1).join(', ')} et ${noms[noms.length - 1]}`;
};

const ModalAjouterEleve = ({ famille, onClose, onSuccess, anneeScolaireId }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [elevesDisponibles, setElevesDisponibles] = useState([]);
  const [eleveSelectionne, setEleveSelectionne] = useState(null);
  const [modeReduction, setModeReduction] = useState('famille');
  const [reductionInscription, setReductionInscription] = useState(0);
  const [reductionMensuel, setReductionMensuel] = useState(0);
  // Reduction supplementaire : le seul mecanisme cumulatif du systeme. Elle vit a
  // cote du mode choisi, pas a sa place, d'ou un etat separe et non un 5e mode.
  const [supplementaireActive, setSupplementaireActive] = useState(false);
  const [supplementaireInscription, setSupplementaireInscription] = useState(0);
  const [supplementaireMensuel, setSupplementaireMensuel] = useState(0);
  const [supplementaireMotif, setSupplementaireMotif] = useState('');
  const [motifAjout, setMotifAjout] = useState('');
  // Le mois d'effet vient du serveur : c'est lui qui connaît les mois déjà
  // engagés, et l'écran ne doit pas en proposer sa propre estimation.
  const [moisEffet, setMoisEffet] = useState(null);
  const [loadingMoisEffet, setLoadingMoisEffet] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [toast, setToast] = useState({ visible: false, message: '', type: 'success' });

  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      if (searchTerm.trim().length < 2) {
        setElevesDisponibles([]);
        return;
      }

      setLoadingSearch(true);
      try {
        const result = await api.familles.getElevesDisponibles(anneeScolaireId, searchTerm.trim());
        setElevesDisponibles(result.success ? result.data : []);
      } catch (error) {
        console.error('Erreur recherche eleves:', error);
        setToast({ visible: true, message: 'Erreur lors de la recherche des élèves', type: 'error' });
      } finally {
        setLoadingSearch(false);
      }
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, anneeScolaireId]);

  const valeurFamilleMensuelle = Number(famille?.reduction_mensuel || 0);
  const familleADesReductions = valeurFamilleMensuelle > 0
    || Number(famille?.reduction_inscription || 0) > 0;
  const reductionModes = useMemo(
    () => getReductionModes(valeurFamilleMensuelle),
    [valeurFamilleMensuelle]
  );

  useEffect(() => {
    if (!eleveSelectionne) return;
    // Sans valeur de famille, « Conserver » est le point de départ honnête :
    // le rattachement seul ne change alors rien à ce que paie l'élève.
    setModeReduction(familleADesReductions ? 'famille' : 'conserver');
    setReductionInscription(0);
    setReductionMensuel(Number(famille?.reduction_mensuel || 0));

    // L'eleve peut deja porter une reduction supplementaire recue a son inscription.
    // On la reprend telle quelle : la laisser vide reviendrait a l'effacer en silence.
    const dejaAccordeeMensuel = Number(eleveSelectionne.reduction_exceptionnelle_mensuel || 0);
    const dejaAccordeeInscription = Number(eleveSelectionne.reduction_exceptionnelle_inscription || 0);
    const dejaAccordee = dejaAccordeeMensuel > 0 || dejaAccordeeInscription > 0;
    setSupplementaireActive(dejaAccordee);
    setSupplementaireInscription(dejaAccordeeInscription);
    setSupplementaireMensuel(dejaAccordeeMensuel);
    setSupplementaireMotif(String(eleveSelectionne.reduction_exceptionnelle_motif || ''));
  }, [eleveSelectionne, famille?.reduction_mensuel, familleADesReductions]);

  useEffect(() => {
    if (!eleveSelectionne) {
      setMoisEffet(null);
      return;
    }

    let annule = false;
    setLoadingMoisEffet(true);
    setMoisEffet(null);

    api.eleves.getMoisEffet(eleveSelectionne.id, anneeScolaireId)
      .then((result) => {
        if (annule) return;
        // En cas d'échec on retombe sur « Automatique » : mieux vaut ne rien
        // annoncer qu'annoncer un mois dont on n'est pas sûr.
        setMoisEffet(result?.success ? result.data : null);
      })
      .catch(() => { if (!annule) setMoisEffet(null); })
      .finally(() => { if (!annule) setLoadingMoisEffet(false); });

    return () => { annule = true; };
  }, [eleveSelectionne, anneeScolaireId]);

  // Reduction principale : une seule des trois sources, elles s'excluent.
  const reductionPrincipaleMensuelle = useMemo(() => {
    if (!eleveSelectionne) return 0;
    if (modeReduction === 'conserver') return Number(eleveSelectionne.reduction_custom_mensuel || 0);
    if (modeReduction === 'famille') return Number(famille?.reduction_mensuel || 0);
    if (modeReduction === 'personnalisee') return Number(reductionMensuel || 0);
    return 0;
  }, [eleveSelectionne, famille?.reduction_mensuel, modeReduction, reductionMensuel]);

  const supplementaireRetenue = supplementaireActive ? Number(supplementaireMensuel || 0) : 0;
  const aDejaUneSupplementaire = Number(eleveSelectionne?.reduction_exceptionnelle_mensuel || 0) > 0
    || Number(eleveSelectionne?.reduction_exceptionnelle_inscription || 0) > 0;

  const calculerMensuel = useCallback((reductionPrincipale, reductionSupplementaire) => {
    const base = Number(eleveSelectionne?.frais_mensuel_normal || 0);
    const totalReduction = Math.min(base, Math.max(0, reductionPrincipale) + Math.max(0, reductionSupplementaire));
    return Math.max(0, Math.round(base - totalReduction));
  }, [eleveSelectionne]);

  const montantCalcule = useMemo(
    () => (eleveSelectionne ? calculerMensuel(reductionPrincipaleMensuelle, supplementaireRetenue) : 0),
    [eleveSelectionne, calculerMensuel, reductionPrincipaleMensuelle, supplementaireRetenue]
  );

  // Calculé en local plutôt qu'attendu du serveur : l'avertissement doit apparaître
  // pendant que la gestionnaire tape le montant, pas une fois la demande envoyée.
  //
  // Il compare deux montants absolus. Une tournure comparative — « paiera X de plus »
  // — se lisait comme une addition aux frais de la famille, c'est-à-dire exactement
  // le contresens que cette alerte existe pour dissiper.
  const avertissementEviction = useMemo(() => {
    if (!eleveSelectionne) return null;
    if (modeReduction !== 'personnalisee') return null;
    const valeurFamille = Number(famille?.reduction_mensuel || 0);
    const personnalisee = Number(reductionMensuel || 0);
    if (valeurFamille <= 0 || personnalisee >= valeurFamille) return null;
    return {
      valeurFamille,
      personnalisee,
      mensuelSiValeurFamille: calculerMensuel(valeurFamille, supplementaireRetenue),
      mensuelSiPersonnalisee: calculerMensuel(personnalisee, supplementaireRetenue)
    };
  }, [eleveSelectionne, calculerMensuel, modeReduction, famille?.reduction_mensuel, reductionMensuel, supplementaireRetenue]);

  // Le même piège dans l'autre sens, et il manquait. L'alerte ci-dessus répond à
  // « auriez-vous mieux fait de reprendre la valeur famille ? ». Elle ne dit rien
  // du cas où le choix retenu fait payer l'élève PLUS QU'AUJOURD'HUI : reprendre
  // une valeur famille de 1 500 quand il a déjà 2 000 à lui le fait monter, alors
  // que le geste ressemble à un rattachement anodin.
  const avertissementHausse = useMemo(() => {
    if (!eleveSelectionne) return null;

    const mensuelActuel = calculerMensuel(
      Number(eleveSelectionne.reduction_custom_mensuel || 0),
      Number(eleveSelectionne.reduction_exceptionnelle_mensuel || 0)
    );

    if (montantCalcule <= mensuelActuel) return null;

    return {
      mensuelActuel,
      mensuelApres: montantCalcule,
      raison: modeReduction === 'famille'
        ? 'la valeur famille remplace sa réduction propre'
        : modeReduction === 'aucune'
          ? 'sa réduction propre est retirée'
          : 'la réduction retenue est plus faible que la sienne'
    };
  }, [eleveSelectionne, calculerMensuel, montantCalcule, modeReduction]);

  const handleAjouter = async () => {
    if (!eleveSelectionne) {
      setToast({ visible: true, message: 'Sélectionnez un élève.', type: 'error' });
      return;
    }

    if (!motifAjout.trim()) {
      setToast({ visible: true, message: 'Le motif est obligatoire pour envoyer la demande.', type: 'error' });
      return;
    }

    const supplementaireDemandee = supplementaireActive
      && (Number(supplementaireInscription || 0) > 0 || Number(supplementaireMensuel || 0) > 0);

    if (supplementaireDemandee && !supplementaireMotif.trim()) {
      setToast({
        visible: true,
        message: 'Indiquez le motif de la réduction supplémentaire.',
        type: 'error'
      });
      return;
    }

    setLoading(true);
    try {
      const result = await api.familles.ajouterEleve(famille.id, {
        eleve_id: eleveSelectionne.id,
        annee_scolaire_id: anneeScolaireId,
        appliquer_reduction_famille: modeReduction === 'famille',
        mode_reduction: modeReduction,
        reduction_inscription: modeReduction === 'personnalisee' ? Number(reductionInscription || 0) : 0,
        reduction_mensuel: modeReduction === 'personnalisee' ? Number(reductionMensuel || 0) : 0,
        reduction_exceptionnelle_inscription: supplementaireDemandee ? Number(supplementaireInscription || 0) : 0,
        reduction_exceptionnelle_mensuel: supplementaireDemandee ? Number(supplementaireMensuel || 0) : 0,
        reduction_exceptionnelle_motif: supplementaireDemandee ? supplementaireMotif.trim() : '',
        // Decocher une supplementaire que l'eleve portait deja est une demande de
        // retrait, pas une absence de choix : sans ce drapeau, elle survivrait.
        retirer_exceptionnelle: aDejaUneSupplementaire && !supplementaireDemandee,
        motif_ajout: motifAjout.trim()
      });

      if (result.success) {
        onSuccess({
          ...result.data,
          validation_required: result.validation_required,
          message: result.message
        });
        return;
      }

      setToast({ visible: true, message: result.message || "Erreur lors de l'ajout de l'élève", type: 'error' });
      setLoading(false);
    } catch (error) {
      console.error('Erreur ajout eleve:', error);
      setToast({ visible: true, message: error.message || "Erreur lors de l'ajout de l'élève", type: 'error' });
      setLoading(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/55 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[94vh] flex flex-col overflow-hidden">
          <div className="p-5 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-[#246990] rounded-lg">
                <User className="text-white" size={22} />
              </div>
              <div>
                <h2 className="font-semibold text-gray-900 text-lg">Ajouter un élève à la famille</h2>
                <p className="text-sm text-gray-600">
                  Famille {famille.nom} - {famille.nombre_enfants || 0} élève(s) actuellement
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="h-10 w-10 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 flex items-center justify-center"
              disabled={loading}
            >
              <X size={22} />
            </button>
          </div>

          <div className="p-6 space-y-5 overflow-y-auto flex-1">
            <div className="grid grid-cols-1 xl:grid-cols-[1.05fr_0.95fr] gap-5">
              <section className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="p-4 bg-[#eef7fb] border-b border-[#246990]/15">
                  <div className="flex items-center gap-2 font-semibold text-[#246990]">
                    <Search size={18} />
                    Élèves seuls disponibles
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Les paiements déjà effectués avant le rattachement resteront historiques.
                  </p>
                </div>

                <div className="p-4 space-y-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Nom, prénom ou matricule…"
                      value={searchTerm}
                      onChange={(event) => setSearchTerm(event.target.value)}
                      disabled={loading}
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15 outline-none disabled:opacity-60"
                    />
                  </div>

                  {loadingSearch && (
                    <div className="py-8 flex justify-center">
                      <LoadingSpinner color="#246990" size={32} />
                    </div>
                  )}

                  {!loadingSearch && searchTerm.trim().length >= 2 && elevesDisponibles.length === 0 && (
                    <div className="p-6 bg-gray-50 rounded-lg text-center text-sm text-gray-500">
                      Aucun élève actif sans famille ne correspond à cette recherche.
                    </div>
                  )}

                  {!loadingSearch && elevesDisponibles.length > 0 && (
                    <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                      {elevesDisponibles.map((eleve) => {
                        const selected = Number(eleveSelectionne?.id) === Number(eleve.id);
                        return (
                          <button
                            key={eleve.id}
                            type="button"
                            onClick={() => !loading && setEleveSelectionne(eleve)}
                            className={`w-full text-left p-4 rounded-lg border transition ${
                              selected
                                ? 'border-[#246990] bg-[#eef7fb] shadow-sm'
                                : 'border-gray-200 hover:border-[#246990]/45 hover:bg-gray-50'
                            }`}
                            disabled={loading}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="font-semibold text-gray-900">
                                    {eleve.prenom} {eleve.nom}
                                  </span>
                                  <span className="px-2 py-0.5 text-xs rounded bg-blue-50 text-[#246990] border border-blue-100">
                                    {eleve.classe_nom}
                                  </span>
                                  <span className="px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-600">
                                    {eleve.matricule}
                                  </span>
                                </div>
                                <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                                  <span className="rounded bg-gray-50 px-3 py-2">
                                    Tarif officiel : <strong>{formatMontant(eleve.frais_mensuel_normal)}/mois</strong>
                                  </span>
                                  <span className="rounded bg-gray-50 px-3 py-2">
                                    Situation actuelle : <strong>{formatMontant(eleve.montant_mensuel_actuel)}/mois</strong>
                                  </span>
                                </div>
                              </div>
                              <span className={`h-7 w-7 rounded-full border flex items-center justify-center ${
                                selected ? 'border-[#246990] bg-[#246990] text-white' : 'border-gray-300 text-transparent'
                              }`}>
                                <Check size={16} />
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </section>

              <section className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="p-4 bg-gray-50 border-b border-gray-200">
                  <div className="flex items-center gap-2 font-semibold text-gray-900">
                    <Calculator size={18} />
                    Règles de rattachement
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Le rattachement sera validé par le Centre de Support avant application.
                  </p>
                </div>

                <div className="p-4 space-y-4">
                  {!eleveSelectionne ? (
                    <div className="p-6 rounded-lg bg-gray-50 text-sm text-gray-500 text-center">
                      Sélectionnez un élève pour définir sa réduction et voir le nouveau montant.
                    </div>
                  ) : (
                    <>
                      <div className="rounded-lg border border-[#246990]/20 bg-[#eef7fb] p-4">
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <div className="text-xs uppercase tracking-wide text-[#246990] font-semibold">Élève sélectionné</div>
                            <div className="font-semibold text-gray-900 mt-1">
                              {eleveSelectionne.prenom} {eleveSelectionne.nom}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-xs text-gray-500">Montant après la demande</div>
                            <div className="text-xl font-bold text-[#246990]">{formatMontant(montantCalcule)}</div>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {reductionModes.map((mode) => {
                          const active = modeReduction === mode.value;
                          return (
                            <button
                              key={mode.value}
                              type="button"
                              onClick={() => setModeReduction(mode.value)}
                              className={`text-left rounded-lg border p-3 transition ${
                                active
                                  ? 'border-[#246990] bg-[#eef7fb] shadow-sm'
                                  : 'border-gray-200 hover:border-[#246990]/40'
                              }`}
                              disabled={loading}
                            >
                              <div className="font-semibold text-gray-900">{mode.title}</div>
                              <div className="text-xs text-gray-600 mt-1">{mode.description}</div>
                            </button>
                          );
                        })}
                      </div>

                      {modeReduction === 'personnalisee' && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <label className="block text-sm">
                            <span className="text-gray-700">Réduction inscription</span>
                            <input
                              type="number"
                              min="0"
                              value={reductionInscription}
                              onChange={(event) => setReductionInscription(event.target.value)}
                              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-[#246990] outline-none"
                            />
                          </label>
                          <label className="block text-sm">
                            <span className="text-gray-700">Réduction mensuelle</span>
                            <input
                              type="number"
                              min="0"
                              value={reductionMensuel}
                              onChange={(event) => setReductionMensuel(event.target.value)}
                              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-[#246990] outline-none"
                            />
                          </label>

                          {/* Une reduction personnalisee REMPLACE la valeur de famille,
                              elle ne s'y ajoute pas. En saisir une plus petite fait donc
                              payer davantage a l'eleve, alors que le geste ressemble a
                              l'octroi d'une remise. C'est le seul piege du systeme qui
                              produise une erreur de facturation par une bonne intention :
                              il doit se voir pendant la saisie, pas apres. */}
                          {avertissementEviction && (
                            <div className="sm:col-span-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5">
                              <div className="flex items-start gap-2">
                                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                                <div className="min-w-0 text-xs leading-snug text-amber-900">
                                  <div className="font-semibold">
                                    Cette réduction remplace la valeur famille, elle ne s'y ajoute pas
                                  </div>
                                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    <div className="rounded border border-amber-200 bg-white px-2.5 py-2">
                                      <div className="text-[11px] text-gray-500">
                                        En reprenant la valeur famille ({formatMontant(avertissementEviction.valeurFamille)})
                                      </div>
                                      <div className="font-semibold text-gray-900">
                                        {formatMontant(avertissementEviction.mensuelSiValeurFamille)} / mois
                                      </div>
                                    </div>
                                    <div className="rounded border border-amber-300 bg-white px-2.5 py-2">
                                      <div className="text-[11px] text-gray-500">
                                        Avec cette réduction ({formatMontant(avertissementEviction.personnalisee)})
                                      </div>
                                      <div className="font-semibold text-gray-900">
                                        {formatMontant(avertissementEviction.mensuelSiPersonnalisee)} / mois
                                      </div>
                                    </div>
                                  </div>
                                  <div className="mt-2 text-[11px]">
                                    Pour accorder une réduction qui <span className="font-semibold">s'ajoute</span> à
                                    celle de la famille, utilisez la réduction supplémentaire ci-dessous.
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Le meme piege dans l'autre sens : reprendre la valeur
                              famille alors que l'eleve a deja mieux le fait payer
                              davantage. Le geste ressemble a un simple rattachement. */}
                          {avertissementHausse && (
                            <div className="sm:col-span-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5">
                              <div className="flex items-start gap-2">
                                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                                <div className="min-w-0 text-xs leading-snug text-amber-900">
                                  <div className="font-semibold">
                                    Ce choix fait payer cet élève davantage qu'aujourd'hui
                                  </div>
                                  <div className="mt-1 text-[11px]">
                                    Parce que {avertissementHausse.raison}.
                                  </div>
                                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    <div className="rounded border border-amber-200 bg-white px-2.5 py-2">
                                      <div className="text-[11px] text-gray-500">Il paie aujourd'hui</div>
                                      <div className="font-semibold text-gray-900">
                                        {formatMontant(avertissementHausse.mensuelActuel)} / mois
                                      </div>
                                    </div>
                                    <div className="rounded border border-amber-300 bg-white px-2.5 py-2">
                                      <div className="text-[11px] text-gray-500">Après l'ajout</div>
                                      <div className="font-semibold text-gray-900">
                                        {formatMontant(avertissementHausse.mensuelApres)} / mois
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* La réduction supplémentaire est le seul mécanisme cumulatif du
                          système, et c'est ce qui décide de son affichage.

                          Elle ne se distingue d'une réduction propre que s'il existe une
                          valeur famille à laquelle s'ajouter : sans elle, « propre 5 000
                          plus supplément 2 000 » et « propre 7 000 » sont le même montant,
                          et proposer les deux serait proposer un choix sans conséquence.

                          On l'affiche donc quand la famille porte une valeur — le supplément
                          s'y ajoute pendant que l'élève continue de suivre cette valeur si
                          elle change — ou quand l'élève en porte déjà une, qu'il faut alors
                          pouvoir ajuster ou retirer. */}
                      {(familleADesReductions || aDejaUneSupplementaire) && (
                      <div className="rounded-lg border border-violet-200 bg-violet-50 p-3">
                        <label className="flex items-start gap-2.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={supplementaireActive}
                            onChange={(event) => setSupplementaireActive(event.target.checked)}
                            disabled={loading}
                            className="mt-0.5 h-4 w-4 rounded border-violet-300 text-violet-600 focus:ring-violet-500"
                          />
                          <span className="min-w-0">
                            <span className="block text-sm font-semibold text-violet-900">
                              Réduction supplémentaire
                            </span>
                            <span className="block text-xs text-violet-800 mt-0.5">
                              Elle <span className="font-semibold">s'ajoute</span> au choix ci-dessus au lieu de le
                              remplacer. C'est la seule réduction qui se cumule.
                            </span>
                          </span>
                        </label>

                        {aDejaUneSupplementaire && (
                          <div className="mt-2 text-[11px] text-violet-700">
                            Cet élève en a déjà une de {formatMontant(eleveSelectionne.reduction_exceptionnelle_mensuel)} / mois
                            {eleveSelectionne.reduction_exceptionnelle_motif
                              ? ` (motif : ${eleveSelectionne.reduction_exceptionnelle_motif})`
                              : ''}
                            . La décocher revient à la retirer.
                          </div>
                        )}

                        {supplementaireActive && (
                          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <label className="block text-sm">
                              <span className="text-violet-900">Supplément inscription</span>
                              <input
                                type="number"
                                min="0"
                                value={supplementaireInscription}
                                onChange={(event) => setSupplementaireInscription(event.target.value)}
                                disabled={loading}
                                className="mt-1 w-full px-3 py-2 border border-violet-200 rounded-lg bg-white focus:border-violet-500 outline-none"
                              />
                            </label>
                            <label className="block text-sm">
                              <span className="text-violet-900">Supplément mensuel</span>
                              <input
                                type="number"
                                min="0"
                                value={supplementaireMensuel}
                                onChange={(event) => setSupplementaireMensuel(event.target.value)}
                                disabled={loading}
                                className="mt-1 w-full px-3 py-2 border border-violet-200 rounded-lg bg-white focus:border-violet-500 outline-none"
                              />
                            </label>
                            <label className="block text-sm sm:col-span-2">
                              <span className="text-violet-900">
                                Motif de la réduction supplémentaire <span className="text-red-500">*</span>
                              </span>
                              <input
                                value={supplementaireMotif}
                                onChange={(event) => setSupplementaireMotif(event.target.value)}
                                placeholder="Ex : enfant du personnel, situation sociale documentée…"
                                disabled={loading}
                                className="mt-1 w-full px-3 py-2 border border-violet-200 rounded-lg bg-white focus:border-violet-500 outline-none"
                              />
                            </label>
                            {supplementaireRetenue > 0 && (
                              <div className="sm:col-span-2 text-xs text-violet-800">
                                {formatMontant(reductionPrincipaleMensuelle)} du choix ci-dessus
                                {' + '}{formatMontant(supplementaireRetenue)} de supplement
                                {' = '}
                                <span className="font-semibold">
                                  {formatMontant(Math.min(
                                    Number(eleveSelectionne.frais_mensuel_normal || 0),
                                    reductionPrincipaleMensuelle + supplementaireRetenue
                                  ))} de réduction mensuelle
                                </span>
                                {(reductionPrincipaleMensuelle + supplementaireRetenue) > Number(eleveSelectionne.frais_mensuel_normal || 0) && (
                                  <span className="block mt-1 text-violet-700">
                                    Le cumul dépasse les frais : la réduction est ramenée au montant des frais.
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      )}

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
                          <div className="text-xs text-gray-500">Avant le rattachement</div>
                          <div className="font-bold text-gray-900">{formatMontant(eleveSelectionne.montant_mensuel_actuel)}</div>
                        </div>
                        <div className="rounded-lg bg-green-50 border border-green-200 p-3">
                          <div className="text-xs text-green-700">Après validation</div>
                          <div className="font-bold text-green-800">{formatMontant(montantCalcule)}</div>
                        </div>
                        <div className="rounded-lg bg-orange-50 border border-orange-200 p-3">
                          <div className="text-xs text-orange-700">Mois d'effet</div>
                          <div className="font-bold text-orange-900">
                            {loadingMoisEffet
                              ? '…'
                              : moisEffet?.mois_effet?.nom
                                || (moisEffet?.aucun_mois_libre ? 'Aucun' : 'Automatique')}
                          </div>
                        </div>
                      </div>

                      {/* Le mois seul ne dit rien : c'est la liste des mois déjà
                          engagés qui explique pourquoi la frontière tombe là. */}
                      {!loadingMoisEffet && moisEffet && (
                        <div className="rounded-lg border border-orange-200 bg-orange-50 p-3 text-xs text-orange-900">
                          {moisEffet.aucun_mois_libre ? (
                            <>
                              Tous les mois de cet exercice ont déjà commencé à être payés. L'élève rejoindra
                              la famille, mais <span className="font-semibold">aucun mois ne sera refacturé</span>
                              {' '}au nouveau tarif cette année.
                            </>
                          ) : moisEffet.mois_engages?.length > 0 ? (
                            <>
                              Le nouveau tarif s'applique à partir de{' '}
                              <span className="font-semibold">{moisEffet.mois_effet.nom}</span>.{' '}
                              {listerMois(moisEffet.mois_engages.map((m) => m.mois))}
                              {moisEffet.mois_engages.length > 1
                                ? ' ont déjà été réglés : ils gardent leur montant'
                                : ' a déjà été réglé : il garde son montant'}
                              {' '}d'origine. {moisEffet.mois_effet.nom} est le premier mois dont le paiement
                              n'a pas commencé.
                            </>
                          ) : (
                            <>
                              Le nouveau tarif s'applique dès{' '}
                              <span className="font-semibold">{moisEffet.mois_effet.nom}</span>, le premier mois
                              de l'année : cet élève n'a encore réglé aucun mois.
                            </>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  <label className="block">
                    <span className="text-sm font-medium text-gray-700">
                      Motif de la demande <span className="text-red-500">*</span>
                    </span>
                    <textarea
                      value={motifAjout}
                      onChange={(event) => setMotifAjout(event.target.value)}
                      placeholder="Ex : rattachement familial confirmé, dossier parent complété…"
                      rows={4}
                      disabled={loading}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15 outline-none resize-none disabled:opacity-60"
                    />
                  </label>
                </div>
              </section>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="rounded-lg border border-orange-200 bg-orange-50 p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-orange-900">
                    <div className="font-semibold">Lecture comptable</div>
                    <p className="mt-1">
                      Les anciens paiements individuels de l'élève ne seront pas modifiés. À partir du mois d'effet, les paiements suivants seront suivis dans la famille.
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-[#246990]/20 bg-[#eef7fb] p-4">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="w-5 h-5 text-[#246990] mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-[#174866]">
                    <div className="font-semibold">Validation obligatoire</div>
                    <p className="mt-1">
                      L'ajout modifie les obligations financières de la famille. La demande sera traitée dans le Centre de Support.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="p-5 bg-gray-50 border-t border-gray-200 flex items-center justify-between gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={loading}
              className={`${BOUTON_PIED_CLASSES} border-gray-300 hover:bg-gray-100`}
            >
              Annuler
            </Button>
            <Button
              onClick={handleAjouter}
              disabled={!eleveSelectionne || loading}
              className={`${BOUTON_PIED_CLASSES} bg-[#246990] hover:bg-[#1f5a7a] text-white font-semibold`}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Envoi en cours…
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4" />
                  Envoyer la demande
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {toast.visible && (
        <ToastPortal>
          {shouldUseLongToast(toast.message, toast.type, toast) ? (
            <LongToast
              show
              title={toast.title}
              message={toast.message}
              type={toast.type}
              onClose={() => setToast({ ...toast, visible: false })}
            />
          ) : (
            <Toast
              message={toast.message}
              type={toast.type}
              onClose={() => setToast({ ...toast, visible: false })}
            />
          )}
        </ToastPortal>
      )}
    </>
  );
};

export default ModalAjouterEleve;
